import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/services/api';

/**
 * Error code the backend answers (as a 409) when DV360 changed since the
 * report was built. Other 409s (already decided, being applied, report too
 * old) carry their own codes and are shown with their message.
 */
export const STALE_SUGGESTION_CODE = 'SUGGESTION_STALE';

const NOT_FOUND_STATUS = 404;

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === NOT_FOUND_STATUS;
}

/** A missing report is an answer, not a failure: don't retry it. */
function retryUnlessNotFound(failureCount: number, error: Error): boolean {
  return !isNotFound(error) && failureCount < 2;
}

/**
 * Saved optimization reports of an execution, newest first. An execution
 * nobody has run the delivery-report skill on answers 404, read as no reports.
 */
export function useOptimizationReports(executionId: string) {
  return useQuery({
    queryKey: ['optimization-reports', executionId],
    queryFn: async () => {
      try {
        return await api.optimizations.listReports(executionId);
      } catch (error) {
        if (isNotFound(error)) return { reports: [] };
        throw error;
      }
    },
    enabled: !!executionId,
    staleTime: 60000,
    retry: retryUnlessNotFound,
  });
}

/** One report with its suggestions and their state. */
export function useOptimizationReport(executionId: string, reportDate: string | null) {
  return useQuery({
    queryKey: ['optimization-report', executionId, reportDate],
    queryFn: () => api.optimizations.getReport(executionId, reportDate as string),
    enabled: !!executionId && !!reportDate,
    staleTime: 60000,
    retry: retryUnlessNotFound,
  });
}

/** Changes written to DV360 (or marked by hand) for the execution, optionally for one line item. */
export function useAppliedOptimizations(executionId: string, lineItemId: string | null) {
  return useQuery({
    queryKey: ['applied-optimizations', executionId, lineItemId ?? 'all'],
    queryFn: () => api.optimizations.listApplied(executionId, lineItemId ?? undefined),
    enabled: !!executionId,
    staleTime: 60000,
    retry: retryUnlessNotFound,
  });
}
